import { useState, useContext } from 'react';
import { AuthContext } from '@/context/auth.context';
import { useStoreOTPSecret } from '@/api/rpc/hooks/otp/useOTPMutations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { T, useGT } from 'gt-react';

/** Standard TOTP algorithm sent to API; not user-facing copy. */
const TOTP_ALGORITHM_SHA1 = 'SHA1';

interface OTPFormDialogProps {
  open: boolean;
  onClose: (refetch?: boolean) => void;
}

export function OTPFormDialog({ open, onClose }: OTPFormDialogProps) {
  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId;
  const gt = useGT();

  const [targetId, setTargetId] = useState('');
  const [name, setName] = useState('');
  const [secret, setSecret] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  function resetForm() {
    setTargetId('');
    setName('');
    setSecret('');
    setErrors({});
  }

  const storeOTPSecret = useStoreOTPSecret({
    onSuccess: () => {
      resetForm();
      onClose(true);
    },
  });

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!targetId.trim()) {
      newErrors.targetId = gt('Target ID is required', {
        context: 'OTP form validation',
      });
    } else if (!/^[a-zA-Z0-9-]+$/.test(targetId)) {
      newErrors.targetId = gt(
        'Only alphanumeric characters and hyphens allowed',
        { context: 'OTP form validation target ID format' },
      );
    } else if (targetId.length > 100) {
      newErrors.targetId = gt('Must not exceed 100 characters', {
        context: 'OTP form validation max length',
      });
    }

    if (!name.trim()) {
      newErrors.name = gt('Name is required', {
        context: 'OTP form validation',
      });
    } else if (name.length > 255) {
      newErrors.name = gt('Must not exceed 255 characters', {
        context: 'OTP form validation max length',
      });
    }

    if (!secret.trim()) {
      newErrors.secret = gt('Secret is required', {
        context: 'OTP form validation',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit() {
    if (!validateForm() || !organizationId) return;

    storeOTPSecret.mutate(
      {
        targetId: targetId.trim(),
        name: name.trim(),
        secret: secret.trim(),
        algorithm: TOTP_ALGORITHM_SHA1,
        digits: 6,
        period: 30,
      },
      {
        onError: (error) => {
          setErrors({
            form:
              error.message ||
              gt('Failed to save OTP secret', {
                context: 'OTP form generic save error',
              }),
          });
        },
      },
    );
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      resetForm();
      onClose(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {gt('Add OTP Secret', {
              context: 'dialog title add one-time password secret',
            })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {errors.form && (
            <p className="text-sm text-destructive">{errors.form}</p>
          )}
          <div className="space-y-2">
            <Label htmlFor="targetId">
              {gt('Target ID', { context: 'OTP form field label' })}
            </Label>
            <Input
              id="targetId"
              placeholder={gt('e.g., salesforce-prod', {
                context: 'OTP target ID example placeholder',
              })}
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
            />
            {errors.targetId && (
              <p className="text-sm text-destructive">{errors.targetId}</p>
            )}
            <p className="text-xs text-muted-foreground">
              <T context="OTP target ID field help text">
                Unique identifier for this OTP target (alphanumeric and hyphens
                only)
              </T>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">
              {gt('Name', { context: 'OTP form display name field' })}
            </Label>
            <Input
              id="name"
              placeholder={gt('e.g., Salesforce Production', {
                context: 'OTP name example placeholder',
              })}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="secret">
              {gt('TOTP Secret', { context: 'OTP form secret field label' })}
            </Label>
            <Input
              id="secret"
              type="password"
              placeholder={gt('Base32-encoded secret', {
                context: 'OTP secret input placeholder',
              })}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
            {errors.secret && (
              <p className="text-sm text-destructive">{errors.secret}</p>
            )}
            <p className="text-xs text-muted-foreground">
              <T context="OTP secret field help text">
                The base32-encoded secret from your authenticator setup
              </T>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              onClose(false);
            }}
          >
            {gt('Cancel', { context: 'OTP form cancel button' })}
          </Button>
          <Button onClick={handleSubmit} disabled={storeOTPSecret.isPending}>
            {storeOTPSecret.isPending
              ? gt('Saving...', { context: 'OTP form save in progress' })
              : gt('Save', { context: 'OTP form save button' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
